import Box from "@mui/material/Box";
import Grid from "components/primitives/Grid";
import moment from "moment";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "components/Icons/CloseIcon";
import Popover from "@mui/material/Popover";
import DateTimePickers, { DateRange, DateRangeValidationError } from "components/mui5/DateTimePickers";
import Mui5 from "components/mui5";
import { memo, useCallback, useMemo, useState } from "react";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

interface PopoverDateRangePickerProps {
  anchorEl: Element | null;
  endDate: moment.Moment;
  open: boolean;
  setPeriod(start: moment.Moment | null, end: moment.Moment | null): void;
  setOpen(open: boolean): void;
  startDate: moment.Moment;
}

interface DateRangeValidationErrorContext {
  validationError: DateRangeValidationError;
}

type DateRangeValue = [moment.Moment | null, moment.Moment | null];

const initialErrors: DateRangeValidationError = [null, null];

function PopoverDateRangePicker(props: PopoverDateRangePickerProps) {
  const [[startDateError, endDateError], setErrors] = useState<DateRangeValidationError>(initialErrors);
  const { anchorEl, endDate, open, setPeriod, setOpen, startDate } = props;
  const { t } = useTranslation();

  const handleUpdate = useCallback(
    ([startDate, endDate]: DateRangeValue, context: DateRangeValidationErrorContext) => {
      if (context.validationError[0] !== null || context.validationError[1] !== null) {
        setErrors(context.validationError);
        return;
      }
      setErrors(initialErrors);
      setPeriod(moment(startDate).startOf("day"), moment(endDate).endOf("day"));
    },
    [setPeriod]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const dateRangeValues = useMemo<DateRange<moment.Moment>>(() => [startDate, endDate], [endDate, startDate]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <Box sx={{ m: 2 }}>
        <Grid container spacing="8px" justifyContent="space-between">
          <Grid>
            <DateTimePickers.MultiInputDateRangeField
              value={dateRangeValues}
              onChange={handleUpdate}
              enableAccessibleFieldDOMStructure={false}
              format="DD / MM / YYYY"
              slots={{
                textField: Mui5.TextField,
                separator: () => <Typography sx={{ textAlign: "center" }}>{t("global.to_preposition")}</Typography>,
              }}
              disableFuture
              slotProps={{
                textField: ({ position }) => ({
                  variant: "outlined",
                  size: "small",
                  error: Boolean(position === "start" ? startDateError : endDateError),
                }),
              }}
            />
          </Grid>
          <Grid>
            <IconButton onClick={handleClose} aria-label={t("global.close_label")} size="small">
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Box sx={{ pt: 2 }}>
          <DateTimePickers.StaticDateRangePicker
            displayStaticWrapperAs="desktop"
            value={dateRangeValues}
            onChange={handleUpdate}
            disableFuture
            slots={{ actionBar: () => null }}
          />
        </Box>
      </Box>
    </Popover>
  );
}

export default memo(PopoverDateRangePicker);
